import { useState, useEffect } from 'react'
import AppLayout from '../components/AppLayout'

interface InstructionsProps {
  onBack: () => void
}

export default function OffersAuditInstructions({ onBack: _onBack }: InstructionsProps) {
  const [activeSection, setActiveSection] = useState<string>('overview')

  const scrollToSection = (id: string, updateHash = true) => {
    const element = document.getElementById(id)
    if (element) {
      const offset = 80
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth',
      })
      setActiveSection(id)
      if (updateHash) {
        window.history.replaceState(null, '', `#${id}`)
      }
    }
  }

  useEffect(() => {
    const hash = window.location.hash.slice(1)
    if (hash) {
      setTimeout(() => scrollToSection(hash, false), 100)
    }
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['overview', 'quick-start', 'excel-format', 'configuration', 'understanding-results', 'known-issues', 'workflows', 'tips']
      const offset = 100
      let current = activeSection

      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 50) {
        current = sections[sections.length - 1]
      } else {
        for (const sectionId of sections) {
          const element = document.getElementById(sectionId)
          if (element) {
            const rect = element.getBoundingClientRect()
            if (rect.top <= offset && rect.bottom > offset) {
              current = sectionId
              break
            }
          }
        }
      }

      if (current !== activeSection) {
        setActiveSection(current)
        window.history.replaceState(null, '', `#${current}`)
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [activeSection])

  const breadcrumbs = [
    { label: 'Offers Audit', href: '/offers-audit/' },
    { label: 'User Guide' },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      {/* Two Column Layout */}
      <div className="max-w-[1400px] mx-auto">
      <div className="grid grid-cols-12 gap-8">
        {/* Left Sidebar Navigation */}
        <aside className="col-span-3">
          <div className="sticky top-20">
            <nav className="card p-4">
              <div className="text-xs font-medium text-linear-text-muted uppercase tracking-wider mb-4 px-2">
                On This Page
              </div>
              <div className="space-y-1">
                {[
                  { id: 'overview', label: 'Overview' },
                  { id: 'quick-start', label: 'Quick Start' },
                  { id: 'excel-format', label: 'Excel Format' },
                  { id: 'configuration', label: 'Configuration' },
                  { id: 'understanding-results', label: 'Reading Results' },
                  { id: 'known-issues', label: 'Known Issues' },
                  { id: 'workflows', label: 'Common Workflows' },
                  { id: 'tips', label: 'Tips' },
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`block w-full text-left px-3 py-2 text-sm rounded-lg transition-all duration-200 ${
                      activeSection === item.id
                        ? 'bg-linear-accent text-white font-medium'
                        : 'text-linear-text-secondary hover:bg-linear-bg-hover hover:text-linear-text'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main className="col-span-9">
          <div className="space-y-8">
            {/* Header */}
            <div className="mb-2">
              <h1 className="text-2xl font-display font-bold text-linear-text mb-3">Offers Audit</h1>
              <p className="text-linear-text-secondary text-base">
                Checks the live RCT deals pages for every region against your OAT export and surfaces only the problems: broken or missing images, data that doesn't match the file, and marketing links that don't resolve. You upload the OAT export, pick your regions, and the tool walks every offer on the site and tells you what's wrong.
              </p>
            </div>

            {/* Overview */}
            <section id="overview" className="scroll-mt-20">
              <div className="card">
                <h2 className="text-xl font-display font-bold text-linear-text mb-4">Overview</h2>
                <div className="space-y-4">
                  <p className="text-linear-text-secondary leading-relaxed">
                    The tool opens each region's live deals pages, reads the data off them, and checks it against your OAT export. It used to diff this month's screenshots against last month's approved baseline, but the RCT site rebuilds top to bottom every month, so a month-old reference never lined up. Now it judges each page on its own: a problem is a problem regardless of what last month looked like. A run that finds nothing comes back empty, and empty means clean.
                  </p>
                  <p className="text-linear-text-secondary leading-relaxed">
                    <strong className="text-linear-text">What it covers:</strong> the homepage, the offers page, and every offer-detail page for the regions you select, in English and Spanish. It checks every offer rather than a sample, because nobody can eyeball a hundred-plus detail pages per region by hand.
                  </p>
                  <div className="bg-linear-bg-hover rounded-lg p-4 border border-linear-border">
                    <p className="text-sm text-linear-text-secondary">
                      BAT (buyatoyota.com) auditing was retired, so the tool runs against RCT only now.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Quick Start */}
            <section id="quick-start" className="scroll-mt-20">
              <div className="card">
                <h2 className="text-xl font-display font-bold text-linear-text mb-6">Quick Start</h2>
                <div className="space-y-3">
                  {[
                    {
                      num: 1,
                      title: 'Upload the OAT Export',
                      desc: 'Drop in the Excel export from OAT. The tool reads your expected offer data from it.',
                    },
                    {
                      num: 2,
                      title: 'Select Regions',
                      desc: 'Pick the TDAs you want to check, or grab them all.',
                    },
                    {
                      num: 3,
                      title: 'Set Languages and Pages',
                      desc: 'English, Spanish or both, and which pages to check.',
                    },
                    {
                      num: 4,
                      title: 'Run the Audit',
                      desc: 'Hit start. The bar shows live progress, including a running "checked X of Y offers" once it reaches the detail pages.',
                    },
                    {
                      num: 5,
                      title: 'Review the Problems',
                      desc: 'Every TDA you audited shows up with a passed/total badge next to its name. The clean ones sit there as a green count, the ones with problems expand so you can work them. Mark anything expected as a known issue, then export the problems.',
                    },
                  ].map((step) => (
                    <div key={step.num} className="group relative">
                      <div className="relative flex items-start gap-4 p-4 rounded-lg border border-linear-border hover:border-linear-border-input hover:bg-linear-bg-hover transition-all">
                        <div className="flex-shrink-0 w-8 h-8 bg-linear-accent text-white rounded-lg flex items-center justify-center font-bold text-sm">
                          {step.num}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-linear-text mb-1">{step.title}</h3>
                          <p className="text-linear-text-muted text-sm">{step.desc}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Excel Format */}
            <section id="excel-format" className="scroll-mt-20">
              <div className="card">
                <h2 className="text-xl font-display font-bold text-linear-text mb-6">Excel Format (OAT Export)</h2>

                <p className="text-linear-text-secondary mb-6 leading-relaxed">
                  You'll need the Excel file exported from OAT (Offer Auditing Tool). It's the source of truth for what each offer should say on the live site.
                </p>

                <div className="space-y-4">
                  {/* RCT Format */}
                  <div className="bg-linear-bg-hover rounded-lg p-5 border border-linear-border">
                    <h3 className="text-lg font-semibold text-linear-text mb-4">Required columns</h3>
                    <div className="bg-linear-bg-input rounded-lg border border-linear-border-input p-4 font-mono text-xs space-y-2">
                      <div className="flex gap-2"><span className="text-linear-accent font-bold">tdaCode</span><span className="text-linear-text-muted">-</span><span className="text-linear-text-secondary">TDA code like SOC, GST or CIN</span></div>
                      <div className="flex gap-2"><span className="text-linear-accent font-bold">offerType</span><span className="text-linear-text-muted">-</span><span className="text-linear-text-secondary">Finance, Lease or Cash</span></div>
                      <div className="flex gap-2"><span className="text-linear-accent font-bold">offerValue</span><span className="text-linear-text-muted">-</span><span className="text-linear-text-secondary">The offer value or rate</span></div>
                      <div className="flex gap-2"><span className="text-linear-accent font-bold">expirationDate</span><span className="text-linear-text-muted">-</span><span className="text-linear-text-secondary">When the offer expires</span></div>
                    </div>
                  </div>

                  {/* Detection note */}
                  <div className="bg-linear-bg-hover rounded-lg p-4 border border-linear-border">
                    <div className="font-semibold text-linear-text mb-2">A quick check on upload</div>
                    <p className="text-sm text-linear-text-secondary">
                      When you drop the file in, the tool confirms it looks like an RCT export and warns you before you continue if the columns don't line up.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Configuration */}
            <section id="configuration" className="scroll-mt-20">
              <div className="card">
                <h2 className="text-xl font-display font-bold text-linear-text mb-6">Configuration</h2>

                <div className="grid grid-cols-1 gap-4">
                  {[
                    {
                      title: 'Regions',
                      items: [
                        'The tool audits the regional content on <code class="bg-linear-bg-input px-1 rounded text-linear-accent text-xs">toyota.com/{tda}/deals-incentives/</code>',
                        'Spanish uses the <code class="bg-linear-bg-input px-1 rounded text-linear-accent text-xs">/espanol/</code> prefix, e.g. <code class="bg-linear-bg-input px-1 rounded text-linear-accent text-xs">toyota.com/espanol/socal/deals-incentives/</code>',
                        'Examples: SOC (Southern California), GST (Gulf States), CIN (Cincinnati)',
                        'Hit "Select All" to check every region at once',
                      ],
                    },
                    {
                      title: 'Languages',
                      items: [
                        'English (EN)',
                        'Spanish (ES)',
                        'Or run both in the same audit',
                      ],
                    },
                    {
                      title: 'Pages',
                      items: [
                        '<strong class="text-linear-text">Homepage</strong> - the main landing page',
                        '<strong class="text-linear-text">Offers</strong> - where all the offers are listed',
                        '<strong class="text-linear-text">Offer Details</strong> - the individual offer pages, checked one by one for every offer',
                      ],
                    },
                  ].map((config, idx) => (
                    <div key={idx} className="bg-linear-bg-hover rounded-lg p-5 border border-linear-border">
                      <h3 className="text-lg font-semibold text-linear-text mb-3">{config.title}</h3>
                      <ul className="space-y-2">
                        {config.items.map((item, itemIdx) => (
                          <li key={itemIdx} className="flex items-start gap-2 text-sm text-linear-text-secondary">
                            <svg className="w-5 h-5 text-success mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span dangerouslySetInnerHTML={{ __html: item }} />
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Understanding Results */}
            <section id="understanding-results" className="scroll-mt-20">
              <div className="card">
                <h2 className="text-xl font-display font-bold text-linear-text mb-6">Reading Your Results</h2>

                {/* Every TDA shows with a passed/total badge */}
                <div className="bg-linear-bg-hover rounded-lg p-5 border border-linear-border mb-6">
                  <h3 className="text-lg font-semibold text-linear-text mb-3">Every TDA shows, problems expand</h3>
                  <p className="text-linear-text-secondary text-sm leading-relaxed mb-3">
                    Each TDA you audited gets its own row, and next to its name is a mono passed/total count of its offers. When everything checked out you see something like "126/126" in green with a Passed chip, and that's the whole story: a passing TDA sits there as a clean count and doesn't expand, since there's nothing underneath to open. Clicking it does nothing.
                  </p>
                  <p className="text-linear-text-secondary text-sm leading-relaxed">
                    A TDA with problems reads more like "103/104" in amber or red. Click the row to drop into the Data Issues table for that region, where each row is one problem on one offer. Hover the count if you forget what it measures, the tooltip spells it out: offers without findings over offers checked. So the passing regions give you coverage at a glance and the ones that need work are the only ones you actually click into.
                  </p>
                </div>

                {/* Status Badges */}
                <div className="space-y-3 mb-6">
                  {[
                    {
                      badge: 'WARNED',
                      bgClass: 'badge-warning',
                      title: 'Worth a look',
                      desc: 'Something is off but probably not a blocker, like a broken image in a secondary module, a marketing link that doesn\'t resolve, or a region you picked that wasn\'t in the OAT file',
                    },
                    {
                      badge: 'FAILED',
                      bgClass: 'badge-error',
                      title: 'Real problem',
                      desc: 'Data doesn\'t match the OAT file, or a required image is missing where one is expected',
                    },
                    {
                      badge: 'ERROR',
                      bgClass: 'badge-error',
                      title: 'Couldn\'t check it',
                      desc: 'The page didn\'t load or the offer was pulled, so the tool couldn\'t finish its checks',
                    },
                  ].map((status, idx) => (
                    <div key={idx} className={`flex items-start gap-4 p-4 rounded-lg border border-linear-border bg-linear-bg-hover`}>
                      <span className={`badge ${status.bgClass}`}>
                        {status.badge}
                      </span>
                      <div>
                        <strong className="text-linear-text">{status.title}</strong>
                        <p className="text-linear-text-muted text-sm">{status.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Screenshots */}
                <div className="bg-linear-bg-hover rounded-lg p-5 border border-linear-border mb-6">
                  <h3 className="text-lg font-semibold text-linear-text mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5 text-info" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Screenshots
                  </h3>
                  <p className="text-linear-text-secondary text-sm leading-relaxed">
                    Every flagged offer carries a screenshot of the live page as proof, so you can see the problem without opening the site yourself. Clean offers are counted but not captured, which is what keeps a full, every-offer run fast.
                  </p>
                </div>

                {/* Data Issues table */}
                <div>
                  <h3 className="text-lg font-semibold text-linear-text mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5 text-error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Working the Data Issues table
                  </h3>
                  <div className="space-y-4 text-sm text-linear-text-secondary leading-relaxed">
                    <p>
                      The Data Issues table is where the work happens. Each row is one problem on one offer, and the columns tell you where it lives: the Page it showed up on, the offer Type (APR, Lease, Cash, a category name, or Multi Vehicle when one offer spans several models), the Vehicle it affects, a short standardized Issue label, and the offer ID. The offer ID is a link straight to that specific offer's page, so one click takes you to the exact offer on the live site. Rows that group a tab or a category rather than a single offer, like an offer-type or a vehicle-category grouping, link to the region's offers page instead, since there's no one offer to land on. Rows whose ID reads "Not Live" are region-level summaries of offers whose vehicle isn't on the site yet, and those link nowhere because there's no page behind them. Click any header to sort, so you can group every APR problem together or line a whole region up by ID for your tracker. A multi-vehicle offer shows up as "Multiple" in the Vehicle column since there's no single model to name.
                    </p>
                    <p>
                      Click a row to open it. The expanded view carries the full finding text and, when there is one, a screenshot of the live page as proof. Hover the thumbnail for a magnifier, which opens full-screen on desktop and in a new tab on a phone. A row that is self-explanatory, like an offer page that no longer loads, has no arrow because there is nothing more to show.
                    </p>
                    <p>
                      The Issue label is standardized so the same problem reads the same way every run: "Marketing link broken," "Offer page not found," "Expiration date mismatch," "Missing vehicle image," "Broken image on offer page," "Missing College/Military badge," "Vehicle not MYCOed yet." Anything that doesn't fit a named bucket lands under "Other." Broken links and offer pages are clickable straight from the row, so one click confirms the 404 or drops you on the offer.
                    </p>
                    <p>
                      On a narrow window or a phone the table folds into cards that hold the same information, with severity and the Known checkmark sitting up on the issue line.
                    </p>
                  </div>
                </div>

                {/* RCT-specific checks */}
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-linear-text mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5 text-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5.07 19h13.86A2 2 0 0020.66 16L13.73 4a2 2 0 00-3.46 0L3.34 16A2 2 0 005.07 19z" />
                    </svg>
                    The checks you'll see
                  </h3>
                  <p className="text-linear-text-secondary text-sm mb-3">
                    Here is what each check looks for. The bold heading on each is the standardized label you'll see in a row. The line under it is the underlying detection, which carries more detail than the label itself.
                  </p>
                  <div className="space-y-2 text-sm">
                    <div className="bg-linear-bg-hover rounded-lg p-3 border border-linear-border">
                      <span className="text-linear-accent text-xs font-semibold">Offer page not found</span>
                      <p className="text-linear-text-muted text-xs mt-1">
                        The detail page didn't come back with real content, or it served toyota.com's "Page Not Found" body with an HTTP 200. Usually the offer was pulled or its URL changed since the OAT export. The tool separates these from vehicles that were never on the site, so a missing page here means the offer itself is the question.
                      </p>
                    </div>
                    <div className="bg-linear-bg-hover rounded-lg p-3 border border-linear-border">
                      <span className="text-linear-accent text-xs font-semibold">Vehicle not MYCOed yet</span>
                      <p className="text-linear-text-muted text-xs mt-1">
                        One row per region, ID reads "Not Live," that gathers up the offers whose vehicle or model year isn't on the site. The OAT export starts carrying next-year rows weeks before the changeover happens, and those detail pages 404 until it does, which used to bury a region under dozens of "not found" warnings. The row's issue text lists which vehicles were skipped and how many offers each. When a run starts before the month's offers rotate in at all, you'll see a single "Offers not live yet" callout for the region instead: re-run after rotation.
                      </p>
                    </div>
                    <div className="bg-linear-bg-hover rounded-lg p-3 border border-linear-border">
                      <span className="text-linear-accent text-xs font-semibold">Expiration date mismatch &amp; Offer value mismatch</span>
                      <p className="text-linear-text-muted text-xs mt-1">
                        Every offer detail page has a URL with an ID that changes each month, so the tool reads the live page and checks it against your OAT row: the expiration date lines up, and the APR rate or cash amount actually shows up. A file date of 06-01-2026 sitting against 07-15-2026 on the page lands here, named by offer.
                      </p>
                    </div>
                    <div className="bg-linear-bg-hover rounded-lg p-3 border border-linear-border">
                      <span className="text-linear-accent text-xs font-semibold">Marketing link broken</span>
                      <p className="text-linear-text-muted text-xs mt-1">
                        The chevron bullet links in the offer body, like "Every new Toyota comes with ToyotaCare" or "Toyota Safety Sense," get checked to make sure they resolve. A dead link shows up with its status code and URL, clickable from the row, and its proof screenshot outlines the broken link in red so you can spot it at a glance. The check follows a region-to-root fallback, so a regional link like "/local/college-rebate/" that redirects to a working "/college-rebate/" root page counts as fine rather than a false break. The check runs inside the page session, so toyota.com's bot-blocking of our IP can't produce a false 404, and links it can't reach at all get skipped rather than flagged. Some breaks come from a hidden space at the start of the link's address in OAT, which makes the browser glue the address onto the page URL. The link looks fine on the page and the mangled URL won't appear anywhere in its source, so the finding spells out the cause and where the link was meant to go. And since one bad OAT link renders on every offer in a region, it reports once with an offer count instead of thirty times.
                      </p>
                    </div>
                    <div className="bg-linear-bg-hover rounded-lg p-3 border border-linear-border">
                      <span className="text-linear-accent text-xs font-semibold">Missing vehicle image</span>
                      <p className="text-linear-text-muted text-xs mt-1">
                        The detail page rendered no vehicle photo where one should be. Often a real authoring gap, sometimes a rendering hiccup worth a manual look. The flagged offer comes with a screenshot.
                      </p>
                    </div>
                    <div className="bg-linear-bg-hover rounded-lg p-3 border border-linear-border">
                      <span className="text-linear-accent text-xs font-semibold">Broken image on offer page</span>
                      <p className="text-linear-text-muted text-xs mt-1">
                        A detail page can load its hero fine and still have a dead image further down, and for a while those hid behind the ones that loaded. The tool now walks everything rendered, shadow DOM included, and flags any image that finished loading with zero width while occupying real space. The finding lists the exact image URLs so you can verify without hunting the page.
                      </p>
                    </div>
                    <div className="bg-linear-bg-hover rounded-lg p-3 border border-linear-border">
                      <span className="text-linear-accent text-xs font-semibold">Missing College/Military badge</span>
                      <p className="text-linear-text-muted text-xs mt-1">
                        A college or military offer that isn't showing its Military Rebate or College Rebate badge gets called out per offer, checked on each detail page so it names the exact offer and comes with a screenshot of it. The badge hydrates several seconds after the rest of the page, so the tool waits for it before deciding anything, which ended the false callouts we chased in mid-July. The detail-page read is the authoritative one now, so the older region-level card check stays quiet when full every-offer coverage is on. That way the same missing badge is one finding, not two. Usually marketing hasn't set the image in OAT yet and the vehicle render is sitting there instead.
                      </p>
                    </div>
                    <div className="bg-linear-bg-hover rounded-lg p-3 border border-linear-border">
                      <span className="text-linear-accent text-xs font-semibold">Missing ID.me link</span>
                      <p className="text-linear-text-muted text-xs mt-1">
                        College and military offers should link out to ID.me for eligibility. If the tool doesn't find one on those offers, you'll see this line. It runs on every region except SET.
                      </p>
                    </div>
                    <div className="bg-linear-bg-hover rounded-lg p-3 border border-linear-border">
                      <span className="text-linear-accent text-xs font-semibold">VCR fallback image</span>
                      <p className="text-linear-text-muted text-xs mt-1">
                        On the Special tab an offer should show its own image rather than the generic vehicle render. When the tool sees the color picker, the color-walk disclaimer, and the color info together, that's the VCR fallback kicking in because no image was set in OAT. It only flags this on the Special tab, since the color-walk render is normal everywhere else.
                      </p>
                    </div>
                    <div className="bg-linear-bg-hover rounded-lg p-3 border border-linear-border">
                      <span className="text-linear-accent text-xs font-semibold">Broken carousel image</span>
                      <p className="text-linear-text-muted text-xs mt-1">
                        The featured-vehicles carousel at the top of the deals page gets scanned for broken images, jellies and lifestyle shots both. A region that runs text-only cards won't trip this, since there's no image to break.
                      </p>
                    </div>
                    <div className="bg-linear-bg-hover rounded-lg p-3 border border-linear-border">
                      <span className="text-linear-accent text-xs font-semibold">Broken category image / price</span>
                      <p className="text-linear-text-muted text-xs mt-1">
                        Each vehicle in the Explore by category section should show a price and a working image. A missing MSRP or a broken jelly turns up here as a heads-up for the CMs, and it never fails a run on its own.
                      </p>
                    </div>
                    <div className="bg-linear-bg-hover rounded-lg p-3 border border-linear-border">
                      <span className="text-linear-accent text-xs font-semibold">BAT link on RCT page</span>
                      <p className="text-linear-text-muted text-xs mt-1">
                        A link on the page routes to <code className="bg-linear-bg-input px-1 rounded text-linear-accent text-xs">buyatoyota.com</code> instead of the toyota.com destination, for KBB, Toyota Safety Sense, or Toyota Care. Toyota.com destinations don't trigger this.
                      </p>
                    </div>
                    <div className="bg-linear-bg-hover rounded-lg p-3 border border-linear-border">
                      <span className="text-linear-accent text-xs font-semibold">Broken change-vehicle image</span>
                      <p className="text-linear-text-muted text-xs mt-1">
                        On a detail page the tool opens the Change Vehicle picker and checks that each vehicle jelly in the list loads. A broken one shows up here, named by vehicle, as a heads-up for the CMs, and it never fails a run on its own.
                      </p>
                    </div>
                    <div className="bg-linear-bg-hover rounded-lg p-3 border border-linear-border">
                      <span className="text-linear-accent text-xs font-semibold">Not in OAT file</span>
                      <p className="text-linear-text-muted text-xs mt-1">
                        You picked a TDA to audit, but the uploaded OAT export has no rows for it, so the tool had nothing to check its offers, dates, values, or images against. Rather than let that slip by as a green pass, it flags the region as a warning so you know to re-run with an export that includes it.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Known Issues */}
            <section id="known-issues" className="scroll-mt-20">
              <div className="card">
                <h2 className="text-xl font-display font-bold text-linear-text mb-6">Managing Known Issues</h2>

                <p className="text-linear-text-secondary mb-4 leading-relaxed">
                  Some findings are expected, and you want a record that someone already looked at them. Marking one as known files it in a per-region Known Issues list that sits below the results and tags it in the export. The row itself stays in the table, so this is a running log of what has been triaged rather than a filter that hides things.
                </p>

                <div className="bg-warning/10 border border-warning/30 rounded-lg p-5 mb-6">
                  <h4 className="font-semibold text-linear-text mb-2">A note on monthly rotation and baselines</h4>
                  <p className="text-linear-text-secondary text-sm leading-relaxed mb-3">
                    One thing to keep in mind with RCT: the offers rotate every month and each one carries a fresh ID, so a known mark you put on a specific offer this month will not line up against next month's offers. Marking known is mainly a within-audit tool, tidying the list you are working right now. A recurring pattern tied to a region rather than a single offer, like a college badge that has been missing all quarter, is the kind of thing that can carry across runs.
                  </p>
                  <p className="text-linear-text-secondary text-sm leading-relaxed">
                    There is no saved baseline behind any of this. The tool used to keep a baseline screenshot per offer and diff against it, but the offers change monthly, so there was never a stable image to compare. Instead each offer is checked against your OAT row and the live page directly, which is why a clean run needs nothing from last month.
                  </p>
                </div>

                <div className="bg-linear-bg-hover rounded-lg p-5 border border-linear-border mb-6">
                  <h3 className="font-semibold text-linear-text mb-4 text-lg">How to Tag a Known Issue</h3>
                  <ol className="space-y-3">
                    {[
                      'Find the issue in your results',
                      'Click "Mark as Known Issue"',
                      'Pick a type: Data Mismatch, Visual Difference, and so on',
                      'Write a short note so the next auditor knows why, e.g. "Promo not in OAT yet"',
                      'It gets recorded in the per-region Known Issues list and marked in the export',
                    ].map((step, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-sm text-linear-text-secondary">
                        <span className="flex-shrink-0 w-6 h-6 bg-linear-bg-input text-linear-text rounded-full flex items-center justify-center text-xs font-semibold border border-linear-border">
                          {idx + 1}
                        </span>
                        <span className="pt-0.5">{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>

                <div className="bg-linear-bg-hover rounded-lg p-5 border border-linear-border">
                  <h4 className="font-semibold text-linear-text mb-3">When to use this:</h4>
                  <div className="space-y-2">
                    {[
                      'Regional variations that are intentional and expected',
                      'OAT got updated but the site deploy hasn\'t gone out yet, or the other way around',
                      'A flagged item you\'ve already confirmed is fine',
                    ].map((useCase, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-sm text-linear-text-secondary">
                        <svg className="w-5 h-5 text-success mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>{useCase}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Common Workflows */}
            <section id="workflows" className="scroll-mt-20">
              <div className="card">
                <h2 className="text-xl font-display font-bold text-linear-text mb-6">Common Workflows</h2>

                <div className="grid grid-cols-1 gap-4">
                  {[
                    {
                      title: 'Monthly Offers Audit',
                      steps: [
                        'Grab the latest OAT export at the start of the month',
                        'Upload your Excel file',
                        'Select all the regions and both languages',
                        'Run the full audit',
                        'Work through the problems, failures first',
                        'Mark known issues so they don\'t clutter the next run',
                        'Export as PDF to share with your team. Exports hold the problems only, so passing offers stay out, and every flagged offer comes with its screenshot',
                      ],
                    },
                    {
                      title: 'Spot Check After a Change',
                      steps: [
                        'Pick only the regions that changed',
                        'Choose the relevant pages (Offers plus Offer Details is usually enough)',
                        'Run a quick audit',
                        'Confirm the problems it finds match what you expected from the change',
                      ],
                    },
                    {
                      title: 'Historical Review',
                      steps: [
                        'Click "View History" to open the history panel',
                        'Toggle "Mine" for your audits or "All" for the whole team',
                        'Load any past run to inspect its problems and screenshots',
                        'Hit "Load more" to page through older runs',
                        'Look for regions that keep turning up the same problem month after month',
                      ],
                    },
                  ].map((workflow, idx) => (
                    <div key={idx} className="bg-linear-bg-hover rounded-lg p-5 border border-linear-border">
                      <h3 className="font-semibold text-linear-text mb-4 text-lg">
                        {workflow.title}
                      </h3>
                      <ol className="space-y-2">
                        {workflow.steps.map((step, stepIdx) => (
                          <li key={stepIdx} className="flex items-start gap-3 text-sm text-linear-text-secondary">
                            <span className="flex-shrink-0 w-6 h-6 bg-linear-bg-input text-linear-text rounded-full flex items-center justify-center text-xs font-semibold border border-linear-border">
                              {stepIdx + 1}
                            </span>
                            <span className="pt-0.5">{step}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Tips */}
            <section id="tips" className="scroll-mt-20">
              <div className="card">
                <h2 className="text-xl font-display font-bold text-linear-text mb-6">Tips That Save You Time</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    {
                      title: 'Results Land When the Run Finishes',
                      desc: 'The page holds results until the audit completes, so you see the full picture at once instead of a half-filled list that keeps shifting while workers report in.',
                    },
                    {
                      title: 'Watch the Live Count',
                      desc: 'Once the audit reaches the detail pages, the bar shows "checked X of Y offers" and climbs as it goes, so a long run isn\'t a mystery. Every offer gets checked, which is why a full region takes a few minutes.',
                    },
                    {
                      title: 'Run Early in the Month',
                      desc: 'New offers go live at the start of each month, so that\'s when you\'ll catch the most.',
                    },
                    {
                      title: 'Keep Your OAT Export Fresh',
                      desc: 'Stale Excel data is the number one cause of false positives. Pull a fresh export right before you run.',
                    },
                    {
                      title: 'Model Year vs Offer Date',
                      desc: 'The year in an offer title ("Tundra 2025") is the vehicle model year from Toyota\'s feed rather than the offer date. Your export can have both 2025 and 2026 model-year vehicles on the same 2026 offer dates.',
                    },
                    {
                      title: 'Test a Subset First',
                      desc: 'Run two or three regions before kicking off all of them. It catches config mistakes early.',
                    },
                    {
                      title: 'Write Good Known-Issue Notes',
                      desc: 'Your future self, and whoever audits next, will thank you for a clear one-line reason.',
                    },
                    {
                      title: 'Share Results',
                      desc: 'Hit share to copy a direct link, anyone on the team can open it and land on the same report. For sharing outside the team, export the PDF instead.',
                    },
                    {
                      title: 'Exports Carry the Problems Only',
                      desc: 'CSV, JSON and PDF all export the non-passing results, so passing offers and passing TDAs stay out of the file. What you hand off is the punch list, not the full run.',
                    },
                    {
                      title: 'PDF Export Includes Screenshots',
                      desc: 'Each flagged offer gets a page in the PDF with its screenshot and the issue list, so the findings explain themselves. The download link is good for 15 minutes, so save the file right away.',
                    },
                    {
                      title: 'Marketing Links Get Checked',
                      desc: 'The bullet links in each offer body are verified live, so a typo in an OAT link surfaces as a broken-link finding instead of slipping through to the customer.',
                    },
                  ].map((tip, idx) => (
                    <div key={idx} className="bg-linear-bg-hover rounded-lg p-4 border border-linear-border">
                      <h3 className="font-semibold text-linear-text mb-1">{tip.title}</h3>
                      <p className="text-sm text-linear-text-muted leading-relaxed">{tip.desc}</p>
                    </div>
                  ))}
                </div>

                {/* Final Important Note */}
                <div className="mt-6 bg-linear-bg-hover rounded-lg p-5 border border-linear-border">
                  <h3 className="font-bold text-linear-text mb-2 text-lg">Your Judgment Still Matters</h3>
                  <p className="text-linear-text-secondary leading-relaxed">
                    A flagged offer isn't always a bug. A promo that isn't in OAT yet, an intentional regional choice, or the gap between an OAT update and a site deploy all show up as problems that are working as intended. The tool finds them fast, but you're the one who decides whether something needs fixing. Trust what you know about the release.
                  </p>
                </div>
              </div>
            </section>

          </div>
        </main>
      </div>
      </div>
    </AppLayout>
  )
}
